import { useFormik } from "formik";
import * as Yup from "yup";
import React from "react";
import { toast } from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";

import Button from "components/common/Button";
import TextInput from "components/common/InputField/TextInput";
import { getTextFieldProps } from "helpers/getTextFieldProps";
import CustomToast from "components/common/CustomToast";
import { useSpaceGuestLogin, useSpaceGuestTokenVerify } from "hooks/spaceHooks";
import * as UrlConstants from "constants/urls";
import LoadingContent from "components/common/LoadingContent";
import Loader from "components/common/Loader";
import SpaceGuestAuthLayout from "../SpaceGuestAuthLayout";

export default function SpacesGuestLogin() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const token = searchParams.get("token");

    const [spaceGuestLoginMutation, spaceGuestLoginMutationResult] = useSpaceGuestLogin();
    const getSpaceTokenVerifyQuery = useSpaceGuestTokenVerify({ token }, { skip: !token });

    const formik = useFormik({
        initialValues: {
            email: "",
        },
        validationSchema: Yup.object({
            email: Yup.string().email("Invalid email address").required("Email is required").trim(),
        }),
        onSubmit: async (values) => {
            try {
                await spaceGuestLoginMutation(values);

                toast.custom((t) => (
                    <CustomToast t={t} message="Email sent successfully" type="success" />
                ));
                navigate(UrlConstants.SPACES_GUEST_LOGIN_EMAIL_SENT, {
                    state: {
                        email: values.email,
                    },
                });
            } catch (error) {
                toast.custom((t) => <CustomToast t={t} message={error?.message} type="error" />);
            }
        },
    });

    if (token) {
        if (getSpaceTokenVerifyQuery.isSuccess) {
            navigate(UrlConstants.SPACES_GUEST);
        }
        return (
            <LoadingContent
                loading={getSpaceTokenVerifyQuery.isLoading}
                error={getSpaceTokenVerifyQuery.isError}
                renderLoading={() => (
                    <div className="h-screen w-screen flex items-center justify-center backdrop-blur-sm bg-[#F3F3F3]">
                        <div className="space-y-4 w-full max-w-[475px] h-full max-h-[337px] bg-white rounded-[8px] p-[32px] flex flex-col items-center justify-center">
                            <Loader />

                            <div>
                                <p className="text-2xl font-rocGroteskMedium text-center">
                                    Verifying guest login...
                                </p>
                                <p className="text-sm text-slate-400 font-rocGroteskMedium text-center">
                                    Please wait while we verify your guest login.
                                </p>
                            </div>
                        </div>
                    </div>
                )}
                renderError={() => (
                    <div className="h-screen w-screen flex flex-col items-center justify-center backdrop-blur-sm bg-[#F3F3F3]">
                        <div className="space-y-4 w-full max-w-[475px] h-full max-h-[337px] bg-white rounded-[8px] p-[32px] flex flex-col items-center justify-center">
                            <i className="ri-error-warning-fill text-[#CC0000] text-2xl"></i>
                            <p>Error verifying guest login</p>
                        </div>
                    </div>
                )}
            >
                <div className="h-screen w-screen flex items-center justify-center backdrop-blur-sm bg-[#F3F3F3]"></div>
            </LoadingContent>
        );
    }

    return (
        <SpaceGuestAuthLayout>
            <form
                onSubmit={formik.handleSubmit}
                className="space-y-4 w-full max-w-[475px] h-full max-h-[337px] bg-white rounded-[8px] p-[32px] flex flex-col items-center justify-center"
            >
                <div className="flex items-center justify-center p-1 bg-y-10 rounded-[8px] w-fit">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="25"
                        height="24"
                        viewBox="0 0 25 24"
                        fill="none"
                    >
                        <path
                            d="M15.6699 7.64258L15.6487 7.67261C18.1243 9.4135 18.7201 12.8316 16.9809 15.3085C15.2417 17.7854 11.8249 18.3816 9.34931 16.6414L5.64844 21.9103C11.0204 25.57 18.3519 24.248 22.1011 18.9097C23.2452 17.2808 23.9237 15.4645 24.1667 13.6181L15.6699 7.64331V7.64258Z"
                            fill="#1E1E1E"
                        />
                        <path
                            d="M9.33017 16.6667L9.3514 16.6367C6.87585 14.8958 6.28002 11.4778 8.0192 9.00082C9.75838 6.52388 13.1752 5.92772 15.6508 7.66787L19.3517 2.39906C13.9797 -1.26069 6.6482 0.0612764 2.89901 5.39966C1.7542 7.02923 1.07566 8.84556 0.833374 10.6912L9.33017 16.666V16.6667Z"
                            fill="#1E1E1E"
                        />
                    </svg>
                </div>

                <div>
                    <p className="text-2xl font-rocGroteskMedium text-center">Welcome back !</p>
                    <p className="text-sm text-slate-400 font-rocGroteskMedium text-center">
                        To re-enter this space, please provide the email you used when signing in.
                        We’ll send you a magic link to join back.
                    </p>
                </div>

                <TextInput
                    {...getTextFieldProps(formik, "email")}
                    inputPlaceholder="Enter your email address"
                    disableNativeValidation
                    name="email"
                />

                <Button
                    btnText="Send Magic Link"
                    type="submit"
                    isLoading={spaceGuestLoginMutationResult?.isLoading}
                />
            </form>
        </SpaceGuestAuthLayout>
    );
}
